import { getApiBase } from '../../utils/apiBase'
import { memberPortalApiFetch } from '../api/memberPortalApiClient'
import { clearClubDisplayCache, clearMemberDisplayCache } from './memberDisplayCache'

export const CLUB_CODE_PATTERN = /^\d{6}$/
export const MEMBER_LAST_CLUB_KEY = 'privana_last_club'
export const MEMBER_ORG_KEY = 'privana_member_org'
export const MEMBER_PROFILE_KEY = 'privana_member_profile'
export const MEMBER_THEME_KEY = 'privana_member_theme'
export const MEMBER_CALLBACK_INFLIGHT_KEY = 'privana_member_callback_inflight'
// Auth0 local-only logout cannot clear the hosted SSO cookie. This durable marker
// blocks restoration and forces prompt=login until a replacement callback settles.
export const MEMBER_LOCAL_LOGOUT_KEY = 'privana_member_local_logout'

export const INVALID_CLUB_ERROR_CODES = Object.freeze([
  'club_not_found',
  'club_deleted',
  'tenant_not_available',
  'members_portal_not_available',
  'member_club_access_invalid',
])

const invalidClubResetPromises = new Map()

const CLUB_CONTEXT_ERROR = {
  UNAVAILABLE: 'club_context_unavailable',
  TEMPORARY: 'club_context_temporary_failure',
}

const MEMBER_API_AUDIENCE =
  (typeof import.meta !== 'undefined' &&
    import.meta.env &&
    (import.meta.env.VITE_AUTH0_API_AUDIENCE || import.meta.env.VITE_AUTH0_AUDIENCE)) ||
  (typeof process !== 'undefined' &&
    process.env &&
    (process.env.REACT_APP_AUTH0_API_AUDIENCE ||
      process.env.VITE_AUTH0_API_AUDIENCE ||
      process.env.VITE_AUTH0_AUDIENCE)) ||
  null

export function isValidClubCode(clubCode) {
  return CLUB_CODE_PATTERN.test(String(clubCode || '').trim())
}

function normalizeClubCodeCandidate(value) {
  const normalized = String(value || '').trim()
  return isValidClubCode(normalized) ? normalized : null
}

function normalizeOrganizationCandidate(value) {
  const normalized = String(value || '').trim()
  return normalized || null
}

function getStorageValue(key) {
  if (typeof window === 'undefined') return null
  try {
    const localValue = window.localStorage?.getItem(key)
    if (localValue) return localValue
    const sessionValue = window.sessionStorage?.getItem(key)
    return sessionValue || null
  } catch { return null }
}

function setStorageValue(key, value) {
  if (typeof window === 'undefined') return

  const normalizedValue = value ? String(value) : ''
  if (!normalizedValue) {
    try {
      window.localStorage?.removeItem(key)
      window.sessionStorage?.removeItem(key)
    } catch { /* persisted bootstrap state is optional */ }
    return
  }

  try {
    window.localStorage?.setItem(key, normalizedValue)
    window.sessionStorage?.setItem(key, normalizedValue)
  } catch { /* persisted bootstrap state is an optimisation */ }
}

function createClubContextError(code) {
  const error = new Error(code)
  error.code = code
  return error
}

export function getStoredMemberOrg() {
  return getStorageValue(MEMBER_ORG_KEY)
}

export function getStoredMemberClubCode() {
  const value = getStorageValue(MEMBER_LAST_CLUB_KEY)
  return normalizeClubCodeCandidate(value)
}

export function persistMemberClubContext({ clubCode, organization }) {
  const normalizedClubCode = normalizeClubCodeCandidate(clubCode)
  const normalizedOrganization = normalizeOrganizationCandidate(organization)

  if (!normalizedClubCode || !normalizedOrganization) {
    return false
  }

  setStorageValue(MEMBER_LAST_CLUB_KEY, normalizedClubCode)
  setStorageValue(MEMBER_ORG_KEY, normalizedOrganization)
  return true
}

export function setStoredMemberClubCode(clubCode) {
  const normalizedClubCode = normalizeClubCodeCandidate(clubCode)
  if (!normalizedClubCode) return false
  setStorageValue(MEMBER_LAST_CLUB_KEY, normalizedClubCode)
  return true
}

export function setStoredMemberOrg(organization) {
  const normalizedOrganization = normalizeOrganizationCandidate(organization)
  if (!normalizedOrganization) return false
  setStorageValue(MEMBER_ORG_KEY, normalizedOrganization)
  return true
}

export function clearStoredMemberOrg() {
  setStorageValue(MEMBER_ORG_KEY, null)
}

export function clearStoredMemberClubContext() {
  setStorageValue(MEMBER_LAST_CLUB_KEY, null)
  setStorageValue(MEMBER_ORG_KEY, null)
}

export function markMemberLocallyLoggedOut() {
  setStorageValue(MEMBER_LOCAL_LOGOUT_KEY, '1')
}

export function clearMemberLocalLogoutMarker() {
  setStorageValue(MEMBER_LOCAL_LOGOUT_KEY, null)
}

export function isMemberLocallyLoggedOut() {
  return getStorageValue(MEMBER_LOCAL_LOGOUT_KEY) === '1'
}

export function getInvalidClubErrorCode(payload) {
  const code = typeof payload?.error === 'string' ? payload.error : payload?.error?.code
  return INVALID_CLUB_ERROR_CODES.includes(code) ? code : null
}

export async function readInvalidClubResponse(response) {
  if (!response || ![403, 404, 410].includes(response.status)) return null
  const payload = await response.clone().json().catch(() => null)
  return getInvalidClubErrorCode(payload)
}

/** Clear only Members Portal tenant state. A stale response is ignored if club selection changed. */
export function resetInvalidMemberClub({ clubCode, clearAuthOrganization } = {}) {
  const normalizedClubCode = normalizeClubCodeCandidate(clubCode)
  if (!normalizedClubCode) {
    return Promise.resolve(false)
  }
  if (invalidClubResetPromises.has(normalizedClubCode)) {
    return invalidClubResetPromises.get(normalizedClubCode)
  }
  if (getStoredMemberClubCode() !== normalizedClubCode) {
    return Promise.resolve(false)
  }

  const resetPromise = (async () => {
    if (getStoredMemberClubCode() !== normalizedClubCode) return false
    clearStoredMemberClubContext()
    if (typeof window !== 'undefined') {
      try {
        window.localStorage?.removeItem(MEMBER_PROFILE_KEY)
        window.localStorage?.removeItem(MEMBER_THEME_KEY)
        window.sessionStorage?.removeItem(MEMBER_PROFILE_KEY)
        window.sessionStorage?.removeItem(MEMBER_THEME_KEY)
        window.sessionStorage?.removeItem(MEMBER_CALLBACK_INFLIGHT_KEY)
      } catch { /* continue the authoritative reset without storage */ }
    }
    clearMemberDisplayCache({ clubCode: normalizedClubCode })
    clearClubDisplayCache(normalizedClubCode)
    await clearAuthOrganization?.()
    return true
  })().finally(() => invalidClubResetPromises.delete(normalizedClubCode))

  invalidClubResetPromises.set(normalizedClubCode, resetPromise)
  return resetPromise
}

export function getClubContextErrorKind(error) {
  if (error?.code === CLUB_CONTEXT_ERROR.UNAVAILABLE) {
    return 'unavailable'
  }
  return 'temporary'
}

export async function resolveMemberClubContextFromCode(clubCode) {
  const normalizedClubCode = normalizeClubCodeCandidate(clubCode)
  if (!normalizedClubCode) {
    throw createClubContextError(CLUB_CONTEXT_ERROR.UNAVAILABLE)
  }

  const base = getApiBase()
  const lookupUrl = `${base}/api/public/club-config/${normalizedClubCode}`
  console.info(
    `[members-mobile-bootstrap] resolving club context clubCode=${normalizedClubCode} apiBase=${
      base || '(relative)'
    } lookupUrl=${lookupUrl}`
  )
  let response
  try {
    response = await fetch(lookupUrl)
  } catch (error) {
    const errorName = typeof error?.name === 'string' ? error.name : '(unknown)'
    const errorMessage = typeof error?.message === 'string' ? error.message : '(no message)'
    console.error(
      `[members-mobile-bootstrap] club context fetch failed clubCode=${normalizedClubCode} apiBase=${
        base || '(relative)'
      } lookupUrl=${lookupUrl} errorName=${errorName} errorMessage=${errorMessage} errorString=${String(
        error
      )}`
    )
    throw createClubContextError(CLUB_CONTEXT_ERROR.TEMPORARY)
  }

  console.info(
    `[members-mobile-bootstrap] club context response received clubCode=${normalizedClubCode} status=${String(
      response.status
    )} ok=${String(response.ok)} url=${response.url || lookupUrl}`
  )

  const invalidCode = await readInvalidClubResponse(response)
  if (invalidCode || response.status === 400) {
    throw createClubContextError(CLUB_CONTEXT_ERROR.UNAVAILABLE)
  }

  if (!response.ok) {
    throw createClubContextError(CLUB_CONTEXT_ERROR.TEMPORARY)
  }

  const payload = await response.json().catch(() => null)
  if (!payload || typeof payload !== 'object') {
    throw createClubContextError(CLUB_CONTEXT_ERROR.TEMPORARY)
  }

  const config = payload?.data || null
  if (!config?.memberPortalEnabled) {
    throw createClubContextError(CLUB_CONTEXT_ERROR.UNAVAILABLE)
  }

  const organization =
    normalizeOrganizationCandidate(config.memberPortalOrgId) ||
    normalizeOrganizationCandidate(config.memberPortalOrgName)

  if (!organization) {
    throw createClubContextError(CLUB_CONTEXT_ERROR.UNAVAILABLE)
  }

  return {
    clubCode: normalizedClubCode,
    organization,
  }
}

export async function resolveAuthenticatedMemberClubCode(getAccessTokenSilently, user) {
  const storedOrganization = getStoredMemberOrg()
  const requestedOrganization =
    normalizeOrganizationCandidate(storedOrganization) ||
    normalizeOrganizationCandidate(user?.org_id) ||
    normalizeOrganizationCandidate(user?.org_name) ||
    null

  const tokenAuthorizationParams = {
    ...(MEMBER_API_AUDIENCE ? { audience: MEMBER_API_AUDIENCE } : {}),
    ...(requestedOrganization ? { organization: requestedOrganization } : {}),
  }

  const lookupUrl = `${getApiBase() || ''}/api/me`
  console.info(
    `[members-mobile-bootstrap] member identity lookup start url=${lookupUrl} requestedAudience=${
      MEMBER_API_AUDIENCE || '(none)'
    } requestedOrganization=${requestedOrganization || '(none)'}`
  )

  let response
  try {
    response = await memberPortalApiFetch(
      getAccessTokenSilently,
      '/api/me',
      { method: 'GET' },
      {
        audience: MEMBER_API_AUDIENCE || undefined,
        tokenAuthorizationParams,
      }
    )
  } catch (error) {
    console.error('[members-mobile-bootstrap] member identity lookup token/fetch failure', {
      requestedAudience: MEMBER_API_AUDIENCE || null,
      requestedOrganization,
      tokenAuthorizationParams,
      errorName: error?.name || null,
      errorMessage: error?.message || String(error),
    })
    throw error
  }

  console.info(
    `[members-mobile-bootstrap] member identity lookup response status=${String(response.status)} ok=${String(
      response.ok
    )} url=${response.url || lookupUrl}`
  )
  if (!response.ok) {
    let responseBody = null
    if (response.status === 403) {
      const contentType = String(response.headers?.get?.('content-type') || '').toLowerCase()
      if (contentType.includes('application/json')) {
        responseBody = await response.json().catch(() => null)
      } else {
        responseBody = await response.text().catch(() => null)
      }
      console.error('[members-mobile-bootstrap] member identity lookup 403 body', responseBody)
    }
    throw new Error(`member_identity_lookup_failed_${response.status}`)
  }
  const payload = await response.json().catch(() => null)

  const candidates = [
    payload?.club_code,
    payload?.clubCode,
    payload?.org_slug,
    Array.isArray(payload?.allowed_clubs) ? payload.allowed_clubs[0] : null,
    Array.isArray(payload?.allowedClubs) ? payload.allowedClubs[0] : null,
    user?.club_code,
    user?.clubCode,
    user?.app_metadata?.club_code,
    user?.['https://privana.app/club_code'],
    user?.['https://privana.club/club_code'],
  ]

  for (const candidate of candidates) {
    const normalized = normalizeClubCodeCandidate(candidate)
    if (normalized) return normalized
  }

  return null
}

export function isCapacitorRuntime() {
  if (typeof window === 'undefined') return false
  const capacitor = window.Capacitor
  if (typeof capacitor?.isNativePlatform === 'function') {
    return Boolean(capacitor.isNativePlatform())
  }
  return Boolean(capacitor?.isNative)
}
